'use client'

import * as React from 'react'
import { motion, useMotionValue, useSpring } from 'framer-motion'
import { cn } from '@/lib/utils'

export type FloatingIconItem = {
  id: number
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  className: string
  /** Stable float cycle duration: avoids Math.random() on each render */
  floatDuration?: number
  /** Single-color lucide icons: brand SVGs carry their own fills */
  iconColor?: string
  /** Tinted tile background, e.g. `#EA433515` */
  tileBackgroundColor?: string
  /** Pixel/brand border accent on the tile */
  tileBorderColor?: string
}

export type FloatingIconsHeroProps = {
  title: React.ReactNode
  subtitle: React.ReactNode
  /** Optional CTA row: marketing sections supply pixel-styled links */
  cta?: React.ReactNode
  icons: FloatingIconItem[]
  className?: string
  contentClassName?: string
  iconTileClassName?: string
  iconClassName?: string
}

type MousePositionRef = React.RefObject<{ x: number; y: number }>

const FloatingIcon = ({
  mouseRef,
  onMouseTick,
  iconData,
  index,
  iconTileClassName,
  iconClassName,
}: {
  mouseRef: MousePositionRef
  onMouseTick: React.MutableRefObject<Set<() => void>>
  iconData: FloatingIconItem
  index: number
  iconTileClassName?: string
  iconClassName?: string
}) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const springX = useSpring(x, { stiffness: 300, damping: 20 })
  const springY = useSpring(y, { stiffness: 300, damping: 20 })
  const IconGlyph = iconData.icon
  const floatDuration = iconData.floatDuration ?? 5 + (iconData.id % 5) * 0.9

  React.useEffect(() => {
    const updatePosition = () => {
      if (!ref.current || !mouseRef.current) return

      const rect = ref.current.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const dx = mouseRef.current.x - centerX
      const dy = mouseRef.current.y - centerY
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance < 150) {
        const angle = Math.atan2(dy, dx)
        const force = (1 - distance / 150) * 50
        x.set(-Math.cos(angle) * force)
        y.set(-Math.sin(angle) * force)
      } else {
        x.set(0)
        y.set(0)
      }
    }

    onMouseTick.current.add(updatePosition)
    return () => {
      onMouseTick.current.delete(updatePosition)
    }
  }, [mouseRef, onMouseTick, x, y])

  return (
    <motion.div
      ref={ref}
      style={{ x: springX, y: springY }}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        delay: index * 0.08,
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      }}
      className={cn('absolute pointer-events-none', iconData.className)}
      aria-hidden
    >
      <motion.div
        className={cn(
          'flex h-14 w-14 items-center justify-center rounded-2xl border border-border/10 bg-card/80 p-2.5 shadow-xl backdrop-blur-md md:h-16 md:w-16 md:rounded-3xl md:p-3',
          iconTileClassName,
        )}
        style={{
          ...(iconData.tileBackgroundColor
            ? { backgroundColor: iconData.tileBackgroundColor }
            : {}),
          ...(iconData.tileBorderColor ? { borderColor: iconData.tileBorderColor } : {}),
        }}
        animate={{
          y: [0, -8, 0, 8, 0],
          x: [0, 6, 0, -6, 0],
          rotate: [0, 5, 0, -5, 0],
        }}
        transition={{
          duration: floatDuration,
          repeat: Infinity,
          repeatType: 'mirror',
          ease: 'easeInOut',
        }}
      >
        <IconGlyph
          className={cn(
            'h-7 w-7 md:h-8 md:w-8',
            !iconData.iconColor && !iconClassName && 'text-foreground',
            iconClassName,
          )}
          style={iconData.iconColor ? { color: iconData.iconColor } : undefined}
        />
      </motion.div>
    </motion.div>
  )
}

const FloatingIconsHero = ({
  className,
  title,
  subtitle,
  cta,
  icons,
  contentClassName,
  iconTileClassName,
  iconClassName,
  ...props
}: FloatingIconsHeroProps & Omit<React.HTMLAttributes<HTMLElement>, 'title'>) => {
  const mouseRef = React.useRef({ x: 0, y: 0 })
  const onMouseTick = React.useRef(new Set<() => void>())

  React.useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      mouseRef.current = { x: event.clientX, y: event.clientY }
      onMouseTick.current.forEach((update) => update())
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  return (
    <section
      className={cn(
        'relative flex w-full min-h-[420px] items-center justify-center overflow-hidden py-12 md:min-h-[520px] md:py-16',
        className,
      )}
      {...props}
    >
      <div className="absolute inset-0 w-full h-full">
        {icons.map((iconData, index) => (
          <FloatingIcon
            key={iconData.id}
            mouseRef={mouseRef}
            onMouseTick={onMouseTick}
            iconData={iconData}
            index={index}
            iconTileClassName={iconTileClassName}
            iconClassName={iconClassName}
          />
        ))}
      </div>

      <div className={cn('relative z-10 px-4 text-center', contentClassName)}>
        {title}
        {subtitle}
        {cta ? <div className="mt-8 flex flex-wrap items-center justify-center gap-3">{cta}</div> : null}
      </div>
    </section>
  )
}

FloatingIconsHero.displayName = 'FloatingIconsHero'

export { FloatingIconsHero }
